import { useCallback, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { useSnackbar } from 'notistack';
import { throwIfAIResponseNotOk } from 'services/ai/ai';
import useLocales from 'hooks/useLocales';

const useGenerateJobDescription = () => {
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [isGenerating, setIsGenerating] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsGenerating(false);
  }, []);

  const generate = useCallback(
    async (jobTitle: string, onUpdate: (text: string) => void) => {
      if (!jobTitle?.trim()) {
        enqueueSnackbar(t('Job title is required to generate a description'), {
          variant: 'warning'
        });
        return;
      }

      let accumulatedDescription = '';

      try {
        setIsGenerating(true);
        onUpdate('');

        abortRef.current?.abort();
        abortRef.current = new AbortController();

        const accessToken = Cookies.get('accesstoken');
        const response = await fetch('/api/ai/generate_description/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` })
          },
          body: JSON.stringify({ job_title: jobTitle }),
          signal: abortRef.current.signal
        });

        await throwIfAIResponseNotOk(response);

        if (!response.body) {
          const text = await response.text();
          onUpdate(text);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let streamDone = false;
        while (!streamDone) {
          const { done, value } = await reader.read();

          if (done) {
            const finalChunk = decoder.decode();
            if (finalChunk) {
              accumulatedDescription += finalChunk;
              onUpdate(accumulatedDescription);
            }
            streamDone = true;
            continue;
          }

          const decodedChunk = decoder.decode(value, { stream: true });
          if (!decodedChunk) {
            continue;
          }

          accumulatedDescription += decodedChunk;
          onUpdate(accumulatedDescription);
        }

        reader.releaseLock();
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('Error generating job description:', error);
        enqueueSnackbar(
          error instanceof Error
            ? error.message
            : t('Failed to generate job description'),
          { variant: 'error' }
        );
      } finally {
        setIsGenerating(false);
        abortRef.current = null;
      }
    },
    [enqueueSnackbar, t]
  );

  return { generate, abort, isGenerating };
};

export default useGenerateJobDescription;
